import { Form, Formik, FormikConfig, FormikHelpers, FormikProps } from 'formik';
import FormGroupComponent, { InputType } from '@spa/components/form-group/form-group.component';
import React, { CSSProperties, ReactElement } from 'react';

export type WithHoneypot<T> = T & { [key in string]: string };

type ValueOrPromise<T> = T | Promise<T>;

type SubmitOverride<T> = (
  values: WithHoneypot<T>,
  helpers: FormikHelpers<T>
) => ValueOrPromise<void>;

interface FormikWithHoneyProps<T extends object> extends Omit<
  FormikConfig<T>,
  'initialValues' | 'onSubmit'
> {
  readonly children: React.ReactElement | ((props: FormikProps<T>) => React.ReactElement);
  readonly initialValues: T;
  readonly honeypotToken: string;
  readonly onSubmit: SubmitOverride<T>;
  readonly formClass?: string;
}

const FormikWithHoney = <T extends object>({
  children,
  initialValues,
  onSubmit,
  honeypotToken,
  formClass,
  ...restProps
}: FormikWithHoneyProps<T>): ReactElement => {
  const hiddenControl: CSSProperties = {
    position: 'absolute',
    left: '-9999px',
    height: 0,
    width: 0,
    overflow: 'hidden'
  };

  const spicedInitialValues: WithHoneypot<T> = {
    ...initialValues,
    [honeypotToken]: ''
  };

  return (
    <Formik<WithHoneypot<T>>
      {...(restProps as FormikConfig<WithHoneypot<T>>)}
      initialValues={spicedInitialValues}
      onSubmit={
        onSubmit as (values: WithHoneypot<T>, helpers: FormikHelpers<WithHoneypot<T>>) => void
      }
    >
      {(formikBag) => (
        <Form className={formClass} noValidate>
          <div style={hiddenControl} aria-hidden="true" tabIndex={-1}>
            <FormGroupComponent
              id={honeypotToken}
              label={honeypotToken}
              name={honeypotToken}
              type={InputType.TEXT}
              tabIndex={-1}
            />
          </div>
          {typeof children === 'function' ? children(formikBag as FormikProps<T>) : children}
        </Form>
      )}
    </Formik>
  );
};

export default FormikWithHoney;
